import * as React from 'react';
import './RenameInput.scss';

export type RenameInputProps = {
  initialName: string;
  /** Validation or API message for the rename in progress. */
  error?: string | null;
  /** True while the rename request is in flight. */
  isSaving?: boolean;
  /** Called with the new name when the rename is confirmed (Enter / blur). */
  onCommit: (name: string) => void;
  /** Called when the rename is abandoned (Escape). */
  onCancel: () => void;
};

/**
 * Inline rename field. Uncontrolled so keystrokes don't re-render the row:
 * Enter commits, Escape cancels, and losing focus commits — except when the
 * blur came from selecting text (see below). Pointer/key events are kept from
 * bubbling so typing doesn't select, expand, or drag the tree row.
 *
 * A rejected rename arrives back as `error`. That reopens the field for another
 * attempt — the commit guard is released and focus returned — with the typed
 * value still in place, so nothing has to be retyped.
 */
export const RenameInput: React.FC<RenameInputProps> = ({
  initialName,
  error,
  isSaving,
  onCommit,
  onCancel,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const committedRef = React.useRef(false);
  /** True from a press inside the field until the gesture has fully settled. */
  const selectingRef = React.useRef(false);
  const errorId = React.useId();

  React.useEffect(() => {
    // Defer a frame: the context menu that triggered the rename restores focus
    // to its trigger as it unmounts, which would otherwise steal it back.
    const raf = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  React.useEffect(() => {
    if (!error) return;
    // Let the user try again: blur already fired the failed attempt, so the
    // guard has to be released and focus brought back to the field.
    committedRef.current = false;
    inputRef.current?.focus();
  }, [error]);

  /**
   * Selecting the whole name by dragging often ends with the pointer released
   * outside the field, on the tree row — which takes focus, blurring the input.
   * That blur is part of a selection, not a decision to save, so it is ignored
   * and focus is taken back.
   *
   * The flag has to outlive the pointer-up: the row only claims focus on the
   * click that follows it, so it is cleared a frame later, once that has played
   * out. A press that starts outside the field never sets it, which leaves the
   * ordinary click-away-to-save behaviour intact.
   */
  React.useEffect(() => {
    const endSelection = () => {
      if (!selectingRef.current) return;
      requestAnimationFrame(() => {
        selectingRef.current = false;
        const input = inputRef.current;
        if (input && document.activeElement !== input) input.focus();
      });
    };

    window.addEventListener('pointerup', endSelection);
    window.addEventListener('pointercancel', endSelection);
    return () => {
      window.removeEventListener('pointerup', endSelection);
      window.removeEventListener('pointercancel', endSelection);
    };
  }, []);

  const finish = (commit: boolean) => {
    if (committedRef.current || isSaving) return;
    committedRef.current = true;
    if (commit) onCommit(inputRef.current?.value ?? '');
    else onCancel();
  };

  return (
    <div className="tree-label-rename">
      <input
        ref={inputRef}
        className={
          error
            ? 'tree-label-rename-input tree-label-rename-input--error'
            : 'tree-label-rename-input'
        }
        defaultValue={initialName}
        readOnly={isSaving}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        // Drag sensors activate on pointerdown, not mousedown: without this a
        // drag-select inside the field would also reach the row's drag handle.
        onPointerDown={(e) => {
          selectingRef.current = true;
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') {
            e.preventDefault();
            finish(true);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            finish(false);
          }
        }}
        onBlur={() => {
          // Mid-selection: the gesture handler above will hand focus back.
          if (selectingRef.current) return;
          finish(true);
        }}
      />
      {error && (
        <span id={errorId} role="alert" className="tree-label-rename-error">
          {error}
        </span>
      )}
    </div>
  );
};
